import { useTranslations } from "next-intl";

export type SchoolSetupStep = { key: string; done: boolean; detail: string | null };

const DETAIL_KEYS = ["not_saved", "not_tested", "cannot_open", "key_unavailable", "draft"] as const;

/** F10: one detail string → its sentence (`test_failed:<message>` keeps the message). */
export function useSetupDetailText() {
  const t = useTranslations("platform.school.detail");
  return (detail: string | null): string | null => {
    if (!detail) return null;
    if (detail.startsWith("test_failed:")) {
      return t("testFailed", { message: detail.slice("test_failed:".length) });
    }
    return (DETAIL_KEYS as readonly string[]).includes(detail)
      ? t(detail as (typeof DETAIL_KEYS)[number])
      : null;
  };
}

/**
 * MT-1 Plan 2 S14 (FR-8 UI, AC-87): the eight steps as a list — "Done" / "Missing" as text, never
 * colour alone, plus the detail sentence.
 */
export function SchoolSetupChecklist({ steps }: { steps: SchoolSetupStep[] }) {
  const t = useTranslations("platform.school");
  const detailText = useSetupDetailText();
  return (
    <section className="mt-6" aria-labelledby="school-setup-title">
      <h2 id="school-setup-title" className="text-lg font-semibold">
        {t("checklistTitle")}
      </h2>
      <ul
        aria-labelledby="school-setup-title"
        className="mt-2 space-y-2"
        data-testid="school-setup"
      >
        {steps.map((step) => {
          const sentence = detailText(step.detail);
          return (
            <li
              key={step.key}
              className="rounded-md border border-[var(--border-strong)] px-3 py-2"
              data-testid={`setup-step-${step.key}`}
            >
              <span className="font-medium">
                {t.has(`steps.${step.key}`) ? t(`steps.${step.key}` as "steps.logo") : step.key}
              </span>
              {": "}
              <span className={step.done ? "text-[var(--success)]" : "text-[var(--danger)]"}>
                {step.done ? t("done") : t("missing")}
              </span>
              {sentence && <p className="mt-1 text-sm text-[var(--text-muted)]">{sentence}</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
